import React, { useEffect, useRef, useState } from "react";

/**
 * H2Accent — wraps an <h2> heading with an animated bordeaux underline bar
 * that draws in when the element scrolls into view. Falls back to instant
 * full width if prefers-reduced-motion.
 *
 * Usage:
 *   <H2Accent>
 *     <h2 className="...">My title</h2>
 *   </H2Accent>
 */
export default function H2Accent({ children, width = 72, className = "" }) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setActive(true); return; }

    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setActive(true); },
      { threshold: 0.35 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      {children}
      <div
        aria-hidden="true"
        className="mt-3 h-[5px] rounded-full"
        style={{
          width: active ? `${width}px` : 0,
          background: "linear-gradient(90deg, #8B1E3F 0%, #A22647 100%)",
          transition: "width 1.1s cubic-bezier(0.16, 1, 0.3, 1)",
          boxShadow: active ? "0 4px 14px rgba(139, 30, 63, 0.30)" : "none",
        }}
      />
    </div>
  );
}
